<template>
  <section :class="$style.section">
    <div :class="$style.header">
      <div :class="$style.label">Дата</div>
      <VueDatePicker
        :class="$style.datepicker"
        v-model="customDate"
        :min-date="today"
        :max-date="maxPlanningDate"
        :disabled-week-days="disabledWeekDays"
        :enable-time-picker="false"
        :auto-apply="true"
        :locale="ru"
        :teleport="false"
        :disabled="isScheduleBusy"
        @update:model-value="selectCustomDate"
      >
        <template #trigger>
          <div :class="$style.picker">
            <img src="/icons/picker.svg" alt="" />
            <span :class="$style.pickerValue">
              {{ pickerLabel }}
            </span>
          </div>
        </template>
      </VueDatePicker>
    </div>

    <SectionLoader :active="isSectionLoading">
      <div :class="[$style.dates, { [$style.busy]: isScheduleBusy }]">
        <div
          v-for="date in visibleDates"
          :key="date.value"
          :class="[
            $style.date,
            { [$style.active]: selectedDateValue === date.value },
            {
              [$style.weekend]:
                date.is_disabled && selectedDateValue !== date.value,
            },
          ]"
          @click="selectDate(date)"
        >
          <div :class="[$style.weekday, { [$style.red]: date.is_weekend }]">
            {{ date.weekday }}
          </div>
          <div :class="$style.day">{{ date.day }}</div>
          <div :class="$style.month">{{ date.month }}</div>
        </div>
      </div>
      <div :class="$style.time">Время</div>
      <div
        v-if="timeGroups.length"
        :class="[$style.times, { [$style.busy]: isScheduleBusy }]"
      >
        <div
          v-for="group in timeGroups"
          :key="group.hour"
          :class="$style.timeGroup"
        >
          <button
            type="button"
            :class="[
              $style.hour,
              {
                [$style.selected]: selectedTimeHour === group.hour,
              },
            ]"
            :disabled="isScheduleBusy"
            @click="toggleTimeGroup(group.hour)"
          >
            <span>{{ getGroupLabel(group) }}</span>
            <span
              :class="[
                $style.hourCorner,
                { [$style.selected]: selectedTimeHour === group.hour },
              ]"
            />
          </button>
          <transition name="time-options">
            <div
              v-if="openTimeGroupKey === group.hour"
              :class="$style.optionsMenu"
            >
              <button
                v-for="time in group.times"
                :key="time"
                type="button"
                :disabled="isScheduleBusy"
                @click="selectTime(time)"
                :class="[
                  $style.optionItem,
                  { [$style.optionActive]: selectedTimeValue === time },
                ]"
              >
                {{ formatTime(time) }}
              </button>
            </div>
          </transition>
        </div>
      </div>
      <div v-else :class="$style.notWorked">
        <img src="/icons/i.svg" alt="" />
        Выберите другую дату! На текщий момент сервис полностью загружен, или
        запись временно приастоновлена.
      </div>
    </SectionLoader>
    <div v-if="plannedAtError" :class="$style.error">
      Укажите правильное время
    </div>

    <textarea
      v-model="comment"
      @change="setField('comment', comment)"
      :class="[
        $style.comment,
        {
          [$style.active]: comment.trim(),
          [$style.invalid]: commentError,
        },
      ]"
      placeholder="Добавить комментарий"
    />
    <div v-if="commentError" :class="$style.error">{{ commentError }}</div>
  </section>
</template>

<script>
import { VueDatePicker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { ru } from "date-fns/locale";
import { mapState } from "vuex";
import SectionLoader from "@/components/common/SectionLoader.vue";
import asyncActionMixin from "@/mixins/asyncActionMixin";
import { formatTime } from "@/utils/date";
import { getFirstError } from "@/utils/validation";

const weekdays = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const months = [
  "Янв",
  "Фев",
  "Мар",
  "Апр",
  "Май",
  "Июн",
  "Июл",
  "Авг",
  "Сен",
  "Окт",
  "Ноя",
  "Дек",
];
const compactScreenMaxWidth = 490;
const toBackendWeekday = (jsWeekday) => (jsWeekday + 6) % 7;
const toJsWeekday = (backendWeekday) => (backendWeekday + 1) % 7;

const getDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateFromValue = (value) => {
  const [year, month, day] = value.split("-");
  return new Date(year, month - 1, day);
};

const getDateValueFromDateTime = (value) => {
  if (typeof value !== "string") {
    return getDateValue(new Date(value));
  }

  const match = value.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (match) {
    return match[1];
  }

  return getDateValue(new Date(value));
};

const formatPickerDate = (value) => {
  if (!value) return "";
  const date = getDateFromValue(value);
  return `${date.getDate()} ${months[date.getMonth()].toLowerCase()}`;
};

const getScheduleDate = (date, workDays = [], maxDateValue = "") => {
  const currentDate = new Date(date);
  const dayIndex = currentDate.getDay();
  const backendWeekday = toBackendWeekday(dayIndex);
  const workDay = workDays.find((item) => item.weekday === backendWeekday);
  const value = getDateValue(currentDate);
  return {
    value,
    weekday: weekdays[dayIndex],
    day: currentDate.getDate(),
    month: months[currentDate.getMonth()],
    is_weekend: dayIndex === 0 || dayIndex === 6,
    is_disabled:
      (workDay ? !workDay.is_accept_orders : false) ||
      Boolean(maxDateValue && value > maxDateValue),
  };
};

export default {
  name: "ScheduleSection",
  mixins: [asyncActionMixin],
  components: {
    VueDatePicker,
    SectionLoader,
  },
  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayValue = getDateValue(today);
    return {
      ru,
      today,
      todayValue,
      selectedDateValue: todayValue,
      selectedTimeValue: null,
      openTimeGroupKey: "",
      customDate: null,
      customDateValue: "",
      comment: "",
      isCompactScreen: false,
    };
  },

  computed: {
    ...mapState({
      department: (state) => state.department.department,
      workDays: (state) => state.department.workDays,
      order: (state) => state.orders.order,
      workTimes: (state) => state.orders.workTimes,
      errors: (state) => state.orders.errors,
    }),
    disabledWeekDays() {
      return this.workDays
        .filter((day) => !day.is_accept_orders)
        .map((day) => toJsWeekday(day.weekday));
    },
    planningDepthDays() {
      const value = Number(this.department?.order_planning_depth_days);
      return Number.isFinite(value) && value > 0 ? value : null;
    },
    maxPlanningDate() {
      if (!this.planningDepthDays) {
        return null;
      }

      const date = new Date(this.today);
      date.setDate(date.getDate() + this.planningDepthDays - 1);
      return date;
    },
    maxPlanningDateValue() {
      return this.maxPlanningDate ? getDateValue(this.maxPlanningDate) : "";
    },
    visibleDates() {
      const baseDate = this.customDateValue
        ? getDateFromValue(this.customDateValue)
        : new Date(this.today);
      return Array.from({ length: this.visibleDatesCount }, (_, index) => {
        const date = new Date(baseDate);
        date.setDate(baseDate.getDate() + index);
        return getScheduleDate(date, this.workDays, this.maxPlanningDateValue);
      });
    },
    visibleDatesCount() {
      return this.isCompactScreen ? 5 : 7;
    },
    customDateLabel() {
      return this.customDateValue ? formatPickerDate(this.customDateValue) : "";
    },
    pickerLabel() {
      return formatPickerDate(this.selectedDateValue);
    },
    timeGroups() {
      const groups = this.workTimes.reduce((result, time) => {
        const hour = this.formatTime(time, "HH");
        if (!hour) return result;
        if (!result.has(hour)) {
          result.set(hour, {
            hour,
            label: `${hour}:00`,
            times: [],
          });
        }
        result.get(hour).times.push(time);
        return result;
      }, new Map());
      return Array.from(groups.values());
    },
    selectedTimeHour() {
      return this.selectedTimeValue
        ? this.formatTime(this.selectedTimeValue, "HH")
        : "";
    },
    plannedAtError() {
      return getFirstError(this.errors, ["planned_at"]);
    },
    commentError() {
      return getFirstError(this.errors, ["comment"]);
    },
    isScheduleBusy() {
      return this.isSectionLoading || this.isActionPending;
    },
  },
  watch: {
    workTimes() {
      this.syncOpenTimeGroup();
    },
  },
  mounted() {
    this.syncScreenSize();
    window.addEventListener("resize", this.handleViewportResize);
    this.hydrateFromOrder();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleViewportResize);
  },
  methods: {
    formatTime,
    handleViewportResize() {
      this.syncScreenSize();
      this.syncSelectedDateVisibility();
    },
    syncScreenSize() {
      this.isCompactScreen = window.innerWidth < compactScreenMaxWidth;
    },
    syncSelectedDateVisibility() {
      if (!this.selectedDateValue || this.customDateValue) {
        return;
      }

      if (this.isDateInDefaultRange(this.selectedDateValue)) {
        return;
      }

      this.customDateValue = this.selectedDateValue;
      this.customDate = getDateFromValue(this.selectedDateValue);
    },
    clearPlannedAtSelection() {
      this.selectedTimeValue = null;
      this.openTimeGroupKey = "";
      this.setField("planned_at", null);
    },
    syncManualPlannedAt() {
      if (!this.selectedTimeValue && this.order?.planned_at) {
        this.setField("planned_at", null);
      }
    },
    async ensureOrderId() {
      if (this.order?.id) {
        return this.order.id;
      }

      const result = await this.withSectionLoading(() =>
        this.$store.dispatch("orders/saveOrder")
      );
      const { value, error } = result || {};
      if (error) {
        return null;
      }

      return value?.id || this.$store.state.orders.order?.id || null;
    },
    async fetchWorkTimesForSelectedDate(orderId = this.order?.id) {
      if (!orderId) {
        return;
      }

      await this.withSectionLoading(() =>
        this.$store.dispatch("orders/fetchWorkTimes", {
          date: this.selectedDateValue,
          id: orderId,
        })
      );
    },
    isDateInDefaultRange(value) {
      return Array.from({ length: this.visibleDatesCount }, (_, index) => {
        const date = new Date(this.today);
        date.setDate(this.today.getDate() + index);
        return getDateValue(date);
      }).includes(value);
    },
    async hydrateFromOrder() {
      const plannedAt = this.order?.planned_at;
      this.comment = this.order?.comment || "";

      if (plannedAt) {
        const selectedDateValue = getDateValueFromDateTime(plannedAt);
        this.selectedDateValue = selectedDateValue;

        if (this.isDateInDefaultRange(selectedDateValue)) {
          this.customDate = null;
          this.customDateValue = "";
        } else {
          this.customDate = getDateFromValue(selectedDateValue);
          this.customDateValue = selectedDateValue;
        }
      }

      this.syncSelectedDateVisibility();

      const orderId = await this.ensureOrderId();
      if (orderId) {
        await this.fetchWorkTimesForSelectedDate(orderId);
      }
      this.syncManualPlannedAt();
      this.syncOpenTimeGroup();
    },
    async saveOrderAndFetchTimes() {
      const orderId = await this.ensureOrderId();
      if (orderId) {
        await this.fetchWorkTimesForSelectedDate(orderId);
      }
      this.syncManualPlannedAt();
      this.syncOpenTimeGroup();
    },
    syncOpenTimeGroup() {
      if (!this.timeGroups.length) {
        this.openTimeGroupKey = "";
        return;
      }
      if (
        this.openTimeGroupKey &&
        this.timeGroups.some((group) => group.hour === this.openTimeGroupKey)
      ) {
        return;
      }
      this.openTimeGroupKey = "";
    },
    selectCustomDate(value) {
      if (this.isScheduleBusy || !value) {
        return;
      }
      const scheduleDate = getScheduleDate(
        value,
        this.workDays,
        this.maxPlanningDateValue
      );
      if (scheduleDate.is_disabled) {
        return;
      }
      const dateValue = getDateValue(value);
      this.customDateValue = dateValue;
      this.selectedDateValue = dateValue;
      this.clearPlannedAtSelection();
      this.saveOrderAndFetchTimes();
    },
    selectDate(date) {
      if (this.isScheduleBusy || date.is_disabled) {
        return;
      }
      this.selectedDateValue = date.value;
      this.clearPlannedAtSelection();
      this.saveOrderAndFetchTimes();
    },
    toggleTimeGroup(hour) {
      if (this.isScheduleBusy) {
        return;
      }
      const group = this.timeGroups.find((item) => item.hour === hour);
      if (!group) {
        return;
      }
      if (group.times.length === 1) {
        this.selectTime(group.times[0]);
        return;
      }
      this.openTimeGroupKey = this.openTimeGroupKey === hour ? "" : hour;
    },
    getGroupLabel(group) {
      if (group.times.length === 1) {
        return this.formatTime(group.times[0]);
      }
      if (this.selectedTimeHour === group.hour && this.selectedTimeValue) {
        return this.formatTime(this.selectedTimeValue);
      }
      return group.label;
    },
    setField(field, value) {
      this.$store.commit("orders/setOrderField", { field, value });
    },
    async selectTime(workTime) {
      if (this.isScheduleBusy) {
        return;
      }
      this.selectedTimeValue = workTime;
      this.openTimeGroupKey = "";
      this.setField("planned_at", workTime);

      await this.withAction(() => this.$store.dispatch("orders/saveOrder"));
    },
  },
};
</script>

<style lang="scss" module>
.section {
  padding: 1.5rem;
  @include tablet {
    padding: 1rem;
  }
  .title {
    font-size: 1.3125rem;
    font-weight: 600;
    margin-bottom: 1rem;
    line-height: 1.4;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;

    .label {
      font-size: 1.3125rem;
      font-weight: 600;
      color: $t-primary;
      line-height: 1.4;
    }
  }

  .datepicker {
    width: auto;
    flex-shrink: 0;
    position: relative;
  }

  .picker {
    min-width: 8.875rem;
    min-height: 2.5rem;
    padding: 0.5rem 1rem;
    border: 1px solid $bg-secondary;
    border-radius: 0.25rem;
    background-color: $bg-white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    cursor: pointer;

    img {
      width: 1.5rem;
      height: 1.5rem;
      opacity: 0.24;
      flex-shrink: 0;
    }
  }

  .pickerValue {
    font-size: 1rem;
    color: $t-secondary;
    line-height: 1.5;
    white-space: nowrap;
  }

  .dates {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 0.5rem;
    margin: 0 0 2.5rem 0;

    @include custom(489) {
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }

    .date {
      width: 100%;
      min-width: 0;
      height: 5rem;
      border-radius: 0.5rem;
      padding: 0.5rem;
      border: 1px solid $bg-secondary;
      background-color: $bg-white;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      .weekday {
        font-size: 0.75rem;
        color: $t-secondary;
        margin-bottom: 0.25rem;
      }

      .red {
        color: $orange;
      }

      .day {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 0.25rem;
      }

      .month {
        font-size: 0.75rem;
        color: $t-secondary;
      }
    }

    &.busy {
      pointer-events: none;
    }

    .weekend {
      background-color: $bg-primary;
      border-color: transparent;
      color: $t-secondary;
      pointer-events: none;

      .day,
      .month {
        color: rgba(0, 0, 0, 0.24);
      }
    }

    .active {
      background-color: $t-primary;
      border-color: $t-primary;
      color: $t-white;

      .weekday,
      .month {
        color: rgba(255, 255, 255, 0.5);
      }

      .day {
        color: $t-white;
      }
    }
  }

  .time {
    font-size: 1.3125rem;
    color: $t-primary;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 1.5rem;
  }

  .notWorked {
    font-size: 0.875rem;
    line-height: 1.4;
    padding: 1rem;
    border: 1px solid $orange;
    background-color: $orange-light;
    border-radius: 0.25rem;
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    margin: 0 0 2.5rem 0;
  }

  .times {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.5rem;
    margin-bottom: 2.5rem;
    align-items: start;

    &.busy {
      pointer-events: none;
    }

    @include custom(490) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    @include custom(380) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  .timeGroup {
    position: relative;

    .hour {
      min-height: 2.5rem;
      width: 100%;
      padding: 0 1rem;
      color: $t-secondary;
      background-color: $bg-white;
      border: 1px solid $bg-secondary;
      cursor: pointer;
      border-radius: 0.25rem;
      transition: all 0.3s ease-in-out;
      font-size: 1rem;
      line-height: 1.2;
      font-weight: 400;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;

      &.selected {
        background-color: $t-primary;
        border-color: $t-primary;
        color: $t-white;
        font-weight: 600;
        font-size: 1rem;
        line-height: 1.2;
      }

      &:disabled {
        cursor: not-allowed;
      }
    }
  }

  .hourCorner {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 0.75rem;
    height: 0.75rem;
    transition: transform 0.2s ease, background-color 0.2s ease,
      opacity 0.2s ease;
    background-color: rgba(0, 0, 0, 0.12);
    clip-path: polygon(100% 0, 0 100%, 100% 100%);
    opacity: 0.9;
    &.selected {
      background-color: $green;
      opacity: 1;
    }
  }

  .optionsMenu {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    width: 100%;
    z-index: 6;
    background: $bg-white;
    border: 1px solid $bg-secondary;
    border-radius: 0.25rem;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  .optionItem {
    width: 100%;
    min-height: 2.5rem;
    border: none;
    background: transparent;
    text-align: center;
    padding: 0 1rem;
    font-size: 1rem;
    line-height: 1.4;
    color: $t-secondary;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid $bg-primary;
    display: flex;
    align-items: center;
    justify-content: center;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: $bg-primary;
    }
  }

  .optionActive {
    background-color: $bg-primary;
    color: $t-primary;
    font-weight: 600;
  }

  :global(.time-options-enter-active),
  :global(.time-options-leave-active) {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  :global(.time-options-enter-from),
  :global(.time-options-leave-to) {
    opacity: 0;
    transform: translateY(-0.25rem);
  }
  .error {
    margin: 0.75rem 0;
    color: $red-primary;
    font-size: 0.875rem;
  }
  .comment {
    min-width: 100%;
    max-width: 100%;
    width: 100%;
    height: 6.625rem;
    max-height: 6.625rem;
    min-height: 6.625rem;
    border: 1px solid $bg-primary;
    background-color: $bg-primary;
    border-radius: 0.5rem;
    padding: 0.5rem 1.25rem;
    color: $t-primary;
    font-size: 1rem;
    line-height: 1.5;
    margin: 0;
    &::placeholder {
      color: rgba(0, 0, 0, 0.24);
      font-size: 0.75rem;
      line-height: 1.3;
      font-weight: 400;
    }
    &.active {
      border-color: $bg-secondary;
      background-color: $bg-white;
    }
    &.invalid {
      border-color: $red-primary;
    }
  }
  :global(.dp__theme_light) {
    --dp-background-color: #{$bg-white};
    --dp-text-color: #{$t-primary};
    --dp-hover-color: #{$bg-primary};
    --dp-hover-text-color: #{$t-primary};
    --dp-primary-color: #{$t-primary};
    --dp-primary-text-color: #{$t-white};
    --dp-border-color: #{$bg-secondary};
    --dp-menu-border-color: #{$bg-secondary};
    --dp-border-radius: 0.75rem;
  }
  :global(.dp__menu) {
    font-family: "Inter", sans-serif;
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 0.5rem 2rem 0 #00000014;
  }

  .datepicker :global(.dp__menu) {
    left: auto !important;
    right: 0 !important;
    transform: translateX(-1.5rem) !important;
  }

  .datepicker :global(.dp__button_bottom) {
    display: none !important;
  }
}
</style>
